<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import type { DataStat } from '@/types/landing'
import { useCountUp } from '@/composables/useCountUp'

interface Props {
  stats: DataStat[]
  bottomStat: DataStat
}

const props = defineProps<Props>()
const hasAnimated = ref(false)
const showBottom = ref(false)
const sectionRef = ref<HTMLElement | null>(null)

// 创建 4 个主指标计数动画
const topAnims = props.stats.map((stat) => {
  const { count, start, stop } = useCountUp(stat.value, 2000)
  return { count, start, stop, value: stat.value, suffix: stat.suffix, label: stat.label }
})

// 底部大字号指标计数动画
const { count: bottomCount, start: startBottom, stop: stopBottom } = useCountUp(props.bottomStat.value, 2000)

let observer: IntersectionObserver | null = null

function startAnimations() {
  if (hasAnimated.value) return
  hasAnimated.value = true

  // 启动所有计数动画
  topAnims.forEach((a) => a.start())

  // 底部指标延迟显示和动画
  setTimeout(() => {
    startBottom()
    showBottom.value = true
  }, 2200)
}

onMounted(() => {
  if (!sectionRef.value) return
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          startAnimations()
          observer?.disconnect()
        }
      })
    },
    { threshold: 0.3 },
  )
  observer.observe(sectionRef.value)
})

onUnmounted(() => {
  observer?.disconnect()
  topAnims.forEach((a) => a.stop())
  stopBottom()
})
</script>

<template>
  <section
    id="stats"
    ref="sectionRef"
    class="bg-gradient-to-r from-indigo-600 to-purple-600 py-16 md:py-20"
  >
    <div class="mx-auto max-w-[1200px] px-6">
      <!-- 4 个主指标 -->
      <div class="grid grid-cols-2 gap-8 md:grid-cols-4">
        <div
          v-for="item in topAnims"
          :key="item.label"
          class="text-center"
        >
          <div class="mb-2 text-3xl font-semibold text-white md:text-4xl">
            {{ hasAnimated ? item.count.value : 0 }}{{ item.suffix }}
          </div>
          <div class="text-sm text-white/70 md:text-base">
            {{ item.label }}
          </div>
        </div>
      </div>

      <!-- 底部大字号指标 -->
      <div
        v-if="hasAnimated"
        class="mt-10 text-center transition-all duration-700"
        :class="showBottom ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
      >
        <div class="text-4xl font-bold text-white md:text-5xl">
          {{ showBottom ? bottomCount : 0 }}{{ bottomStat.suffix }}
        </div>
        <div class="mt-2 text-lg text-white/70">
          {{ bottomStat.label }}
        </div>
      </div>
    </div>
  </section>
</template>
